import { TeamType } from "@redotech/account-common/team-type";
import { RateSource } from "@redotech/money/rate-source";
import { useRequiredContext } from "@redotech/react-util/context";
import { getCarrierServiceLabel } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import {
  ShoppedService,
  ShoppedServiceAvailability,
  ShoppedServicesNotice,
  ShoppedServicesNoticeKind,
} from "@redotech/redo-fulfillment-rate-common/shopped-services";
import { useTranslation } from "@redotech/redo-i18n";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import TriangleRight from "@redotech/redo-icons/triangle-right_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { CarrierAccountsContext } from "../carrier-accounts-context";
import * as explainableStyles from "../explainable.module.css";
import { externalBillingBadgeText } from "../external-billing-badge-text";
import { RateChannelBadge } from "../rate-channel-badge";
import { SimpleRateBadge } from "../simple-rate-tooltip/simple-rate-badge";
import {
  filterShoppedServiceGroups,
  isTurnedOffInSettings,
  shoppedServiceGroupLabel,
  shoppedServiceReasonText,
  shoppedServicesEmptyState,
  shoppedServicesSummary,
} from "./shopped-services-view";

const RATES_PENDING_POLL_MS = 2000;

function shoppedServicesPollInterval(
  notice: ShoppedServicesNoticeKind | undefined,
): number | false {
  switch (notice) {
    case undefined:
    case ShoppedServicesNoticeKind.None:
    case ShoppedServicesNoticeKind.NoSnapshot:
    case ShoppedServicesNoticeKind.Precheck:
      return false;
    case ShoppedServicesNoticeKind.RatesPending:
      return RATES_PENDING_POLL_MS;
    default:
      return assertNever(notice);
  }
}

/** Render only while open: each open then starts with an empty search and a fresh request. */
export const AllShoppedServicesModal = memo(function AllShoppedServicesModal({
  fulfillmentGroupId,
  onClose,
}: {
  fulfillmentGroupId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const team = useRequiredContext(TeamContext);
  const viewerIsFulfillmentService =
    team.teamType === TeamType.FulfillmentService;
  const { data, isLoading, isFetching, error, refetch } = useRpcQuery(
    RedoOutboundLabelsRpcClientContext,
    {
      method: "getFulfillmentGroupShoppedServices",
      args: { fulfillmentGroupId },
      // No cached response from a previous open, and a NotFound shows the error state at once.
      options: {
        gcTime: 0,
        retry: false,
        // The server stays pending until a re-rate's snapshot lands, so the list appears without reopening.
        refetchInterval: (rpcQuery) =>
          shoppedServicesPollInterval(rpcQuery.state.data?.notice.kind),
      },
    },
  );
  const allGroups = data?.groups ?? [];
  const visibleGroups = filterShoppedServiceGroups(
    allGroups,
    query,
    viewerIsFulfillmentService,
  );
  const searching = query.trim() !== "";
  const hasServices = allGroups.length > 0;
  const emptyState = data
    ? shoppedServicesEmptyState(allGroups, visibleGroups, data.notice.kind)
    : null;

  return (
    <RedoModal
      contentScrollable
      hideFooter
      isOpen
      onModalCloseRequested={onClose}
      subtitle={t(
        "Every service Redo is shopping for this shipment, including services that cannot be selected.",
      )}
      title={t("All shopped services")}
    >
      {isLoading || (isFetching && (error || !data)) ? (
        <Flex justify="center" p="xl">
          <RedoSpinner size="md" />
        </Flex>
      ) : error || !data ? (
        <RedoEmptyState
          primaryButtonProps={{
            text: t("Try again"),
            onClick: () => void refetch(),
          }}
          subtext=""
          text={t("Couldn't load services.")}
        />
      ) : data.notice.kind === ShoppedServicesNoticeKind.RatesPending ? (
        <Flex align="center" dir="column" gap="md" p="xl">
          <RedoSpinner size="md" />
          <Text fontSize="sm" textColor="secondary">
            {t("Getting updated rates...")}
          </Text>
        </Flex>
      ) : (
        <Flex dir="column" gap="md">
          {hasServices && (
            <RedoTextInput
              fullWidth
              IconLeading={SearchIcon}
              // Keep typing out of the slideout's window shortcuts; Escape must still reach the modal.
              onKeyDown={(event) => {
                if (event.key !== "Escape") {
                  event.stopPropagation();
                }
              }}
              placeholder={t("Search carrier or service")}
              setValue={setQuery}
              size="md"
              trailingActions={
                query ? (
                  <RedoButton
                    aria-label={t("Clear search")}
                    hierarchy="link"
                    IconLeading={XCloseIcon}
                    onClick={() => setQuery("")}
                    size="xs"
                  />
                ) : undefined
              }
              value={query}
            />
          )}
          {hasServices && (
            <Text fontSize="xs" textColor="secondary">
              {shoppedServicesSummary(allGroups, visibleGroups, query)}
            </Text>
          )}
          <ShoppedServicesNoticeLine
            hasServices={hasServices}
            notice={data.notice}
          />
          {emptyState ? (
            <RedoEmptyState
              Icon={SearchIcon}
              iconColor="gray"
              subtext={emptyState.subtext}
              text={emptyState.text}
            />
          ) : (
            visibleGroups.map((group) => {
              const label = shoppedServiceGroupLabel(
                group,
                viewerIsFulfillmentService,
              );
              const count = group.services.length;
              return (
                <RedoCollapseSubsection
                  defaultCollapsed={false}
                  gap="none"
                  key={group.key}
                  renderHeader={({ collapsed, onClick }) => (
                    <Flex align="center" justify="space-between" py="sm">
                      <Flex align="center" gap="sm">
                        <GroupToggle
                          collapsed={collapsed}
                          label={label}
                          onClick={onClick}
                        />
                        <ShippingLogo
                          carrier={group.carrier}
                          size={LogoSize.SMALL}
                        />
                        <Text fontSize="sm" fontWeight="medium">
                          {label}
                        </Text>
                      </Flex>
                      <Text fontSize="xs" textColor="secondary">
                        {searching
                          ? t("{{count}} match(es)", { count })
                          : t("{{count}} service(s)", { count })}
                      </Text>
                    </Flex>
                  )}
                  title={label}
                >
                  {group.services.map((service, index) => (
                    <ShoppedServiceRow
                      key={`${group.key}:${index}`}
                      service={service}
                    />
                  ))}
                </RedoCollapseSubsection>
              );
            })
          )}
        </Flex>
      )}
    </RedoModal>
  );
});

const GroupToggle = memo(function GroupToggle({
  collapsed,
  label,
  onClick,
}: {
  collapsed: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <RedoButton
      aria-expanded={!collapsed}
      aria-label={label}
      hierarchy="link"
      IconLeading={collapsed ? TriangleRight : TriangleDown}
      onClick={onClick}
      size="sm"
    />
  );
});

const ShoppedServicesNoticeLine = memo(function ShoppedServicesNoticeLine({
  hasServices,
  notice,
}: {
  hasServices: boolean;
  notice: ShoppedServicesNotice;
}) {
  const { t } = useTranslation();
  switch (notice.kind) {
    case ShoppedServicesNoticeKind.None:
      return null;
    case ShoppedServicesNoticeKind.NoSnapshot:
      // With nothing listed, the empty state already says to get updated rates.
      if (!hasServices) {
        return null;
      }
      return (
        <Text fontSize="xs" textColor="secondary">
          {t("Get updated rates to see services that couldn't be rated.")}
        </Text>
      );
    case ShoppedServicesNoticeKind.Precheck:
      return (
        <Text fontSize="xs" textColor="error">
          {shoppedServiceReasonText(notice.reason)}
        </Text>
      );
    case ShoppedServicesNoticeKind.RatesPending:
      return null;
    default:
      return assertNever(notice);
  }
});

/** The same badges the Service dropdown uses to tell two prices for one service apart. */
const RatedServiceBadges = memo(function RatedServiceBadges({
  service,
}: {
  service: Extract<
    ShoppedService,
    { availability: ShoppedServiceAvailability.Rated }
  >;
}) {
  const carrierAccounts = useContext(CarrierAccountsContext);
  return (
    <>
      {service.simpleRate && <SimpleRateBadge />}
      {service.channel && <RateChannelBadge channel={service.channel} />}
      {service.price === null && (
        <RedoBadge
          size="xs"
          text={externalBillingBadgeText(
            service.billingAccountId
              ? carrierAccounts?.getBillingAccount(service.billingAccountId)
              : undefined,
          )}
        />
      )}
    </>
  );
});

const ShoppedServiceRow = memo(function ShoppedServiceRow({
  service,
}: {
  service: ShoppedService;
}) {
  const { t } = useTranslation();
  const label = getCarrierServiceLabel(service.carrier, service.service);
  switch (service.availability) {
    case ShoppedServiceAvailability.Rated:
      return (
        <Flex
          align="center"
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          justify="space-between"
          pl="5xl"
          pr="lg"
          py="md"
        >
          <Flex align="center" gap="sm" wrap="wrap">
            <Text fontSize="sm">{label}</Text>
            <RatedServiceBadges service={service} />
          </Flex>
          {service.price && (
            <Text fontSize="sm" fontWeight="medium">
              <MoneyText
                source={RateSource.Stripe}
                value={toMoney({
                  amount: service.price.amount,
                  currency: service.price.currency,
                })}
              />
            </Text>
          )}
        </Flex>
      );
    case ShoppedServiceAvailability.Unavailable:
      return (
        <Flex
          align="center"
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          justify="space-between"
          pl="5xl"
          pr="lg"
          py="md"
        >
          <Flex align="center" gap="sm">
            <Text fontSize="sm">{label}</Text>
            <RedoBadge
              color="gray"
              size="xs"
              text={
                isTurnedOffInSettings(service)
                  ? t("Turned off")
                  : t("Unavailable")
              }
            />
          </Flex>
          <Tooltip
            leftAlign
            placement="top"
            // A carrier that returned several messages sends them one per line.
            title={
              <Text as="span" whiteSpace="pre-line">
                {shoppedServiceReasonText(service.reason)}
              </Text>
            }
          >
            <RedoButton
              buttonClassName={explainableStyles.explainable}
              hierarchy="link"
              size="xs"
              text={t("Why is the service unavailable?")}
            />
          </Tooltip>
        </Flex>
      );
    default:
      return assertNever(service);
  }
});
